import { Card, Divider, Grid, styled, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import _ from 'lodash';
import {
	Fragment,
	Suspense,
	useEffect,
	type CSSProperties,
	type FC,
	type HTMLAttributes,
	type PropsWithChildren,
} from 'react';

import { cardTypes, requestConstants, subscriptionEndings } from '../../../../constants/appConstants';
import { getCurrenciesQuery } from '../../../../queries/currenciesQuery';
import { currencyAndTotalQuery } from '../../../../queries/paymentQuery';
import { userQuery } from '../../../../queries/userQuery';
import type { WalletDetailsRequester } from '../../../../repositories/walletRepository';
import PageLoader from '../../../../router/loaders/pageLoader';
import { formatDate } from '../../../../utils/dateUtils';
import { useZustand } from '../../../../zustandStore';
import type { RequestType, User } from '../../../../zustandStore/slices';

export type Requester = { email: string; firstName: string; lastName: string; properties: any };

const StyledTypography = styled(Typography)<TypographyProps>(ownerState => {
	const { theme } = ownerState;

	return {
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
		fontWeight: theme.typography.tsFontWeight.bold,
		padding: '10px 0 10px 0',
		textAlign: 'right',
	};
});

const StyledTitle = styled(Typography)<TypographyProps>(ownerState => {
	const { theme } = ownerState;

	return {
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
		fontWeight: theme.typography.tsFontWeight.bold,
	};
});

const StyledLabel = styled(Typography)<TypographyProps>(ownerState => {
	const { theme } = ownerState;

	return {
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.small),
		textTransform: 'uppercase',
	};
});

export function getFullName(user: Partial<User> | WalletDetailsRequester | undefined): string {
	if (!user) {
		return '';
	}
	const fullName = `${user.firstName ?? ''}${user.firstName && user.lastName ? ' ' : ''}${user.lastName ?? ''}`.trim();
	if (!fullName) {
		return user.email || '';
	}
	return fullName;
}

type RequestDetailProps = HTMLAttributes<HTMLElement> & {
	requestForApproval?: RequestType;
	style?: CSSProperties;
};

const getStateLabel = (request: RequestType) => {
	return _.get(requestConstants.status, request.state.toLowerCase(), requestConstants.status.default).toUpperCase();
};

const RequestDetail: FC<PropsWithChildren<RequestDetailProps>> = ({ requestForApproval, style }) => {
	const { data: currentUser } = useQuery(userQuery);
	const { data: currencies } = useQuery(getCurrenciesQuery(undefined));
	const purchaseRequest = useZustand(state => state.request.purchaseRequest);
	const showConversationButtons = useZustand(state => state.approvals.showConversationButtons);
	const request = requestForApproval || purchaseRequest;
	const { data: total } = useQuery(
		currencyAndTotalQuery(currencies?.[0] || request?.currency, Number(request?.amount || 0)),
	);
	const stateLabel = getStateLabel(request);
	const { shouldShowCreateCardButton } = useZustand();
	const requestFieldseldEndDate = request.fields?.endDate
		? formatDate(request.fields?.endDate, currentUser?.language)
		: formatDate(dayjs(), currentUser?.language);
	const getCardTypeLabel = () => {
		const cardType = cardTypes.find(key => key.cardType === request.cardType);
		return cardType ? cardType.label : 'Multiple Use';
	};
	const requesterName = getFullName(request.requester);

	useEffect(() => {
		const actionableStates = ['ASSIGNED', 'PENDING', 'CREATED'];
		shouldShowCreateCardButton(showConversationButtons && actionableStates.includes(stateLabel));
	}, [stateLabel, showConversationButtons]);

	const getSubscriptionTypeLabel = () => {
		const endType = subscriptionEndings.find(subscription => subscription.key === request.fields.ends);
		return endType ? endType.label : subscriptionEndings[0]?.label;
	};

	return (
		<Suspense fallback={<PageLoader />}>
			<Card sx={{ width: 300, maxWidth: '100%', padding: 1 }} data-testid="request-detail-content" style={style}>
				<Grid container>
					<Grid size={12}>
						<StyledTitle data-testid="request-title">{request.title}</StyledTitle>
					</Grid>
					<Grid sx={{ pt: 1 }} size={6}>
						<StyledLabel>Status</StyledLabel>
					</Grid>
					<Grid size={6}>
						<Typography ts-request-status="requestStatus" ts-variant={stateLabel} data-testid="request-status">
							{stateLabel}
						</Typography>
					</Grid>
					<Divider sx={{ width: '100%' }} />
					<Grid sx={{ pt: 1 }} size={6}>
						<StyledLabel>Amount</StyledLabel>
					</Grid>
					<Grid size={6}>
						<StyledTypography data-testid="request-amount">{total}</StyledTypography>
					</Grid>
					<Divider sx={{ width: '100%' }} />
					<Grid sx={{ pt: 1 }} size={6}>
						<StyledLabel>Card Type</StyledLabel>
					</Grid>
					<Grid size={6}>
						<StyledTypography data-testid="request-card-type">{getCardTypeLabel()}</StyledTypography>
					</Grid>
					<Divider sx={{ width: '100%' }} />
					{request.fields?.frequency ? (
						<>
							<Grid sx={{ pt: 1 }} size={6}>
								<StyledLabel>Frequency</StyledLabel>
							</Grid>
							<Grid size={6}>
								<StyledTypography data-testid="frequency">{request.fields.frequency}</StyledTypography>
							</Grid>
							<Divider sx={{ width: '100%' }} />
						</>
					) : null}
					{request.fields?.ends ? (
						<>
							<Grid sx={{ pt: 1 }} size={6}>
								<StyledLabel>Subscription Ends</StyledLabel>
							</Grid>
							<Grid size={6}>
								<StyledTypography data-testid="subscription-type">{getSubscriptionTypeLabel()}</StyledTypography>
							</Grid>
							<Divider sx={{ width: '100%' }} />
						</>
					) : null}
					{request.fields?.numberOfPayments ? (
						<>
							<Grid sx={{ pt: 1 }} size={6}>
								<StyledLabel>Number of Payments</StyledLabel>
							</Grid>
							<Grid size={6}>
								<StyledTypography data-testid="number-of-payments">{request.fields.numberOfPayments}</StyledTypography>
							</Grid>
							<Divider sx={{ width: '100%' }} />
						</>
					) : null}
					{request.fields?.endDate ? (
						<>
							<Grid sx={{ pt: 1 }} size={6}>
								<StyledLabel>{'Requested End Date'}</StyledLabel>
							</Grid>
							<Grid size={6}>
								<StyledTypography data-testid="request-end-date">{requestFieldseldEndDate}</StyledTypography>
							</Grid>
							<Divider sx={{ width: '100%' }} />
						</>
					) : null}
					{request.coding
						? Object.keys(request.coding).map(code => (
								<Fragment key={code}>
									<Grid sx={{ pt: 1 }} size={6}>
										<StyledLabel>{request.coding[code]?.listLabel}</StyledLabel>
									</Grid>
									<Grid size={6}>
										<StyledTypography>{request.coding[code]?.path || 'N/A'}</StyledTypography>
									</Grid>
									<Divider sx={{ width: '100%' }} />
								</Fragment>
							))
						: null}
					{requesterName ? (
						<>
							<Grid sx={{ pt: 1 }} size={6}>
								<StyledLabel>Requested By</StyledLabel>
							</Grid>
							<Grid size={6}>
								<StyledTypography data-testid="requester">{requesterName}</StyledTypography>
							</Grid>
						</>
					) : null}
					{request.fields?.comments ? (
						<>
							<Divider sx={{ width: '100%' }} />
							<Grid sx={{ pt: 1 }} size={6}>
								<StyledLabel>Comments</StyledLabel>
							</Grid>
							<Grid size={6}>
								<StyledTypography data-testid="comments">{request.fields?.comments}</StyledTypography>
							</Grid>
						</>
					) : null}
				</Grid>
			</Card>
		</Suspense>
	);
};

export default RequestDetail;
